import _ from "lodash";
import React, { Fragment } from "react";
import PropTypes from "prop-types";
import classes from "./LEPreview.scss";
import { connect } from "react-redux";

import { getAssessmentPreviewQuery } from "Assessments/modules/AssessmentQuery";
import { getAssessmentPreviewDetailsFromCache } from "Assessments/modules/AssessmentGraphqlHelpers";
import { graphql, compose } from "lib/apolloClient";
import withLoader from "UIComponents/GqlWithLoader";
import I18nHOC from "UIComponents/I18nHOC";
import MoreItemWithToolTip from "UIComponents/MoreItemWithToolTip";

import { getPlannerTemplateFromCache } from "modules/CommonGraphqlHelpers";
import { getPlannerTemplateQuery } from "modules/CommonQuery";

import { getElementValue } from "modules/ElementValueUtils";
import { getRethumbUrl, getArchivedText } from "Utils";
import { GetLESteps } from "Utils/GetLESteps";
import FieldComponent from "UnitPlans/components/IBcomponents/FieldComponent";
import LabelComponent from "UnitPlans/components/LabelComponent";
import {
  fontStyle,
  colors,
  PLACEHOLDER_SQUARE_IMAGE,
  PLACEHOLDER_SQUARE_IMAGE_COUNT,
} from "Constants";
import { CURRICULUM_TYPE_DP } from "Constants/stringConstants";
import { getLocalisedTemplate } from "UnitPlans/routes/UnitPlanDetails/routes/EditUnitPlan/routes/IBPlanner/modules/IBPlannerModule";
import { ChevronLeftOutlined } from "@toddle-edu/ds-icons";
import { Tooltip } from "@toddle-edu/ds-web";
import {
  filterFieldsBasedOnConfig,
  getShowAssessmentTool,
  getIsEnabledLTITools,
} from "ClassRoom/modules/utils";
import {
  updateStepList,
  updateAssessmentTemplate,
  updateTemplateForLearningGoals,
  combineUnitPlanMultiFieldData,
  updateCustomLabelsForUBDLearningGoals,
} from "Assessments/modules/Utils";
import ACLStore from "lib/aclStore";

import { getScoreDetailsMemoize } from "ScoreCategories/modules/ScoreCategoriesModule";
import { getUserInfo } from "Login/modules/LoginModule/LoginUtils";
import classNames from "classnames";

import { getCurrentAssessmentTypes } from "Platform/modules/PlatformModule";
import { getFormattedUserName } from "Utils/GetFormattedUserName";
import {
  getSelectedAcademicYearId,
  getUserArchivalStatus,
} from "modules/CommonSelectors";
import { getSubFields } from "UnitPlans/components/LibraryCommonComponents/ResourceEdit/modules/Utils";

const FIELD_TYPE_SPECIFIC_PROPS = {
  FieldDropdown: {
    buttonStyle: {
      padding: 0,
    },
    applyDefaultStyles: false,
  },
};

const styleObj = {
  assessmentToolsConStyle: {
    maxWidth: "575px",
    overflowX: "hidden",
  },
  tooltipContainerStyle: {
    maxHeight: "250px",
    overflowY: "auto",
  },
};

const isCheckValue = ({ value, fieldKey }) => {
  if (fieldKey == "voiceInstruction") {
    return !_.isEmpty(value);
  }
  return true;
};

const UpperCon = React.memo(
  ({ t, LETitle, createdBy, imageURL, id, showAttachmentItemV3 }) => {
    const rethumpUrl = `url(${getRethumbUrl({
      width: 512,
      height: 384,
      imageUrl:
        imageURL ||
        PLACEHOLDER_SQUARE_IMAGE[id % PLACEHOLDER_SQUARE_IMAGE_COUNT],
      fitIn: true,
    })})`;

    const isStaffArchived = getUserArchivalStatus(createdBy);
    const formattedUserName = getFormattedUserName({
      user: createdBy,
      type: "staff",
    });
    const formattedCreatedBy = getArchivedText({
      name: formattedUserName,
      isArchived: isStaffArchived,
      t,
    });

    return (
      <div
        className={classes.upperCon}
        style={{ alignItems: showAttachmentItemV3 && "flex-start" }}
      >
        <div className={classes.BFlexDivCol}>
          {!showAttachmentItemV3 && (
            <div className={classes.previewLabel}>{t("common:preview")}</div>
          )}
          <div className={classes.leTitle}>{LETitle}</div>
          <div className={classes.createdByText}>
            {t("common:by_with_label", {
              label: formattedCreatedBy,
            })}
          </div>
        </div>
        <div
          className={classes.imageContainer}
          style={{
            backgroundImage: rethumpUrl,
          }}
        ></div>
      </div>
    );
  }
);

UpperCon.displayName = "UpperCon";

const UpperConV2 = React.memo(({ moreInfo, LETitle, imageURL, id }) => {
  const moreInfoSize = _.size(moreInfo);
  const rethumpUrl = `url(${getRethumbUrl({
    width: 512,
    height: 384,
    imageUrl:
      imageURL || PLACEHOLDER_SQUARE_IMAGE[id % PLACEHOLDER_SQUARE_IMAGE_COUNT],
    fitIn: true,
  })})`;

  const moreInfoItems = () => {
    return moreInfoSize > 0 ? (
      <div className={classNames("text-body-2", classes.moreInfoContainer)}>
        {_.map(moreInfo, ({ label, items }, index) => {
          return (
            <Fragment key={label}>
              <MoreItemWithToolTip
                items={items}
                tooltipPlacement={"right"}
                length={1}
                moreAll={false}
                trigger={"hover"}
                tooltipContainerStyle={styleObj.tooltipContainerStyle}
                closeOnScroll={true}
                joinSymbol={", "}
              />
              {index !== moreInfoSize - 1 ? (
                <div className={classes.dot} />
              ) : null}
            </Fragment>
          );
        })}
      </div>
    ) : null;
  };

  return (
    <div className={classes.upperConV2}>
      <div
        className={classes.imageContainerV2}
        style={{
          backgroundImage: rethumpUrl,
        }}
      ></div>
      <div className={classes.BFlexDivCol}>
        <Tooltip tooltip={LETitle} placement={"top"}>
          <div className={classNames("heading-4", classes.leTitleV2)}>
            {LETitle}
          </div>
        </Tooltip>
        {moreInfoItems()}
      </div>
    </div>
  );
});

UpperConV2.displayName = "UpperConV2";

const Fields = React.memo(
  ({
    t,
    fields,
    stepLabel,
    field_list,
    assessmentFields,
    unitPlanFields,
    assessmentId,
    portalType,
    attachmentItemHeight,
    showAttachmentItemV3,
    assessmentDetails,
    currentAssessmentTypes,
  }) => {
    return _.map(fields, fieldKey => {
      const field = field_list[fieldKey] ? field_list[fieldKey] : {};
      if (_.includes(["title", "image", "assessmentCategory"], fieldKey)) {
        return null;
      }
      const {
        viewType,
        config: {
          viewLabel,
          label,
          styles,
          options,
          fieldTypeConfig = {},
          plannerElementConfig = {},
        } = {},
      } = field;
      const fieldData = _.find(assessmentFields, { uid: fieldKey });

      const unitPlanFieldValue = _.get(
        _.find(unitPlanFields, { uid: fieldKey }),
        "value",
        ""
      );

      const resolvedValue = _.get(fieldData, "resolvedMinimalTree", "") || {};
      let subjectIds = [];
      if (fieldKey == "benchmarks") {
        subjectIds = _.get(
          _.find(unitPlanFields, { uid: "subjects" }),
          "value",
          ""
        );
      }

      const assessmentFieldValue = _.get(fieldData, "value", "");

      const value = getElementValue({
        fieldUID: fieldKey,
        valueKeys: assessmentFieldValue,
        unitPlanFieldValue,
        fieldObj: field,
        subjectIds: subjectIds,
        resolvedValue: resolvedValue,
        t,
        currentAssessmentTypes,
      });

      const style = {
        ..._.get(styles, "style", {}),
        ..._.get(styles, "viewStyle", {}),
      };

      const checkValue = isCheckValue({
        value,
        fieldKey,
      });
      if (!checkValue) {
        return null;
      }
      const assessmentTools = _.get(assessmentDetails, "assessmentTools");

      // if step label and field label are same, do not show LabelComponent
      const isNotDuplicateLabel =
        !stepLabel ||
        (_.lowerCase(stepLabel) !== _.lowerCase(viewLabel) &&
          _.lowerCase(stepLabel) !== _.lowerCase(label));
      const unitCourseId = _.get(
        assessmentDetails,
        "unitPlan.courses[0].id",
        ""
      );

      return (
        <div key={fieldKey} className={classes.item} style={style}>
          <FieldComponent
            {...fieldTypeConfig}
            defaultOptions={options || []}
            customTextStyle={{
              ...fontStyle.medium,
              color: colors.neutral400,
              fontSize: "1.4rem",
              lineHeight: 1.71,
            }}
            attachments={_.get(fieldData, "attachments", [])}
            type={viewType}
            mode={"view"}
            parentType={"ASSESSMENT"}
            parentId={assessmentId}
            label={viewLabel || label}
            labelComponent={
              isNotDuplicateLabel ? (
                <LabelComponent
                  label={viewLabel || label}
                  labelStyle={{
                    ...fontStyle.demiBold,
                    fontSize: "1.4rem",
                    lineHeight: 1.71,
                    color: colors.blue400,
                  }}
                />
              ) : null
            }
            value={value}
            fieldId={_.get(fieldData, "id", "")}
            fieldKey={fieldKey}
            portalType={portalType}
            attachmentItemHeight={attachmentItemHeight}
            isAssessmentToolPreview={true}
            assessmentToolsConStyle={styleObj.assessmentToolsConStyle}
            showAttachmentItemV3={showAttachmentItemV3}
            isAssessmentCreationRevamp={true}
            assessmentTools={assessmentTools}
            field_list={field_list}
            assessmentFields={assessmentFields}
            unitPlanFields={unitPlanFields}
            {...(FIELD_TYPE_SPECIFIC_PROPS[viewType] || {})}
            plannerElementConfig={plannerElementConfig}
            subFields={getSubFields({
              fieldKey,
              fieldObj: field_list[fieldKey],
              unitPlanFields,
            })}
            unitCourseId={unitCourseId}
          />
        </div>
      );
    });
  }
);

Fields.displayName = "Fields";

const Steps = React.memo(
  ({
    view_steps,
    step_list,
    field_list,
    assessmentFields,
    unitPlanFields,
    unitPlanAllFields,
    assessmentId,
    portalType,
    attachmentItemHeight,
    t,
    showAttachmentItemV3,
    assessmentDetails,
    isDPScoreV2,
    currentAssessmentTypes,
  }) => {
    const assessmentTools = _.get(assessmentDetails, "assessmentTools");

    const { isScoreEnabled } = getScoreDetailsMemoize({
      assessmentTools,
    });

    const someAssessmentToolsHasData = _.some(
      assessmentTools?.edges,
      ({ node }) => {
        const assessmentTool = _.get(node, "assessmentToolItem", {});
        const assessmentToolType = _.get(node, "assessmentToolType", "");

        const isTeacherEvaluationEnable = _.get(
          node,
          "isTeacherEvaluationEnable",
          false
        );
        const isStudentEvaluationEnable = _.get(
          node,
          "isStudentEvaluationEnable",
          false
        );

        return (
          getShowAssessmentTool({
            assessmentTool,
            assessmentToolType,
            allFields: assessmentFields,
          }) &&
          (isTeacherEvaluationEnable || isStudentEvaluationEnable)
        );
      }
    );

    return _.map(view_steps, key => {
      const { label, fields, subtext, viewSubtext } = step_list[key] || {};

      if (
        (isDPScoreV2 && _.includes(fields, "score") && !isScoreEnabled) ||
        (_.includes(fields, "measureAssessing") &&
          !someAssessmentToolsHasData &&
          _.includes(fields, "score") &&
          !isScoreEnabled)
      ) {
        return;
      }

      return (
        <div key={key} className={classes.stepContainer}>
          {label ? (
            <div className={classes.stepLabel}>
              {label}
              {(subtext || viewSubtext) && (
                <div className={classes.stepSubText}>
                  {viewSubtext ?? subtext}
                </div>
              )}
            </div>
          ) : null}
          <div className={classes.formContainer}>
            <Fields
              t={t}
              fields={fields}
              stepLabel={label}
              field_list={field_list}
              assessmentFields={assessmentFields}
              unitPlanFields={unitPlanFields}
              unitPlanAllFields={unitPlanAllFields}
              assessmentId={assessmentId}
              portalType={portalType}
              attachmentItemHeight={attachmentItemHeight}
              showAttachmentItemV3={showAttachmentItemV3}
              assessmentDetails={assessmentDetails}
              currentAssessmentTypes={currentAssessmentTypes}
            />
          </div>
        </div>
      );
    });
  }
);

const LEPreview = React.memo(
  ({
    step_list,
    view_steps,
    field_list,
    assessmentDetails,
    assessmentFields,
    unitPlanFields,
    unitPlanAllFields,
    assessmentId,
    portalType,
    attachmentItemHeight,
    t,
    showAttachmentItemV3,
    showBackButton,
    onClickBackButton,
    innerContainerStyle,
    wrapperStyle,
    isDPScoreV2,
    showNewHeader,
    getCurrentAssessmentTypes,
  }) => {
    const currentAssessmentTypes = getCurrentAssessmentTypes();

    const {
      createdBy,
      title: { value: LETitle } = {},
      image: { value: imageURL } = {},
      id,
      libraryGrades,
      librarySubjects,
    } = assessmentDetails || {};

    const libraryDetails = [
      {
        label: t("common:grade_plural"),
        items: _.map(libraryGrades, "label"),
      },
      {
        label: t("common:subject_plural"),
        items: _.map(librarySubjects, "label"),
      },
    ].filter(({ items }) => !_.isEmpty(items));

    return (
      <div className={classes.container}>
        <div className={classes.innerContainer} style={innerContainerStyle}>
          {showBackButton && (
            <div className={classes.backButtonContainer}>
              <div className={classes.lePreviewHeader}>
                <div
                  className={classes.innerHeader}
                  onClick={onClickBackButton}
                >
                  <div className={classes.backIcon}>
                    <ChevronLeftOutlined size={"xx-small"} variant={"subtle"} />
                  </div>
                  <div className={classes.backText}>{t("common:back")}</div>
                </div>
              </div>
            </div>
          )}
          <div className={classes.wrapper} style={wrapperStyle}>
            {showNewHeader ? (
              <UpperConV2
                LETitle={LETitle}
                imageURL={imageURL}
                id={id}
                moreInfo={libraryDetails}
              />
            ) : (
              <UpperCon
                t={t}
                LETitle={LETitle}
                createdBy={createdBy}
                imageURL={imageURL}
                id={id}
                showAttachmentItemV3={showAttachmentItemV3}
              />
            )}

            <div className={classes.lowerCon}>
              <Steps
                t={t}
                view_steps={view_steps}
                step_list={step_list}
                field_list={field_list}
                assessmentFields={assessmentFields}
                unitPlanFields={unitPlanFields}
                unitPlanAllFields={unitPlanAllFields}
                assessmentId={assessmentId}
                portalType={portalType}
                attachmentItemHeight={attachmentItemHeight}
                showAttachmentItemV3={showAttachmentItemV3}
                assessmentDetails={assessmentDetails}
                isDPScoreV2={isDPScoreV2}
                currentAssessmentTypes={currentAssessmentTypes}
              />
            </div>
          </div>
        </div>
      </div>
    );
  }
);

Steps.displayName = "Steps";

const mapActionCreators = { getCurrentAssessmentTypes };

const mapStateToProps = state => {
  const userType = getUserInfo().user_type;
  const curriculumType = _.get(state, "platform.currentCurriculumProgram.type");
  const isDPScoreV2 =
    _.isEqual(curriculumType, CURRICULUM_TYPE_DP) &&
    ACLStore.can("FeatureFlag:DPScoreCategoriesV2");

  const academicYearId = getSelectedAcademicYearId(state);
  const userArchivedFilters = {
    academicYearId,
  };

  const collaboratorsFilter = {
    userAcademicYearId: academicYearId,
  };

  return {
    userType,
    isDPScoreV2,
    isData: true,
    isLoading: false,
    assessmentType: _.get(state, "assessment.filters.groupType", ""),
    curriculumType,
    plannerElementSets: _.get(state, "platform.currentPlannerElementSets", []),
    userArchivedFilters,
    collaboratorsFilter,
  };
};

const getDynamicTemplate = ({
  template,
  assessmentFields,
  t,
  userType,
  templateId,
  assessmentDetails,
  curriculumType,
  plannerElementSets,
}) => {
  const dynamicTemplate = updateTemplateForLearningGoals({
    template: _.cloneDeep(template),
    templateId,
  });

  const localisedTemplate = getLocalisedTemplate({
    template: { body: dynamicTemplate },
    t,
    templateType: "le",
  });

  if (!_.isEmpty(template)) {
    const step_list = dynamicTemplate["step_list"];
    const field_list = dynamicTemplate["field_list"];

    if (_.includes(["UBD"], curriculumType) && !_.isEmpty(plannerElementSets)) {
      updateCustomLabelsForUBDLearningGoals({
        field_list,
        curriculumType,
        plannerElementSets,
        t,
      });
    }

    const view_steps = _.get(dynamicTemplate, "view_steps", []);

    if (!_.isEmpty(view_steps)) {
      _.forEach(view_steps, step => {
        const stepFields = _.get(step_list, `${step}.fields`, []);
        const filteredStepFields = filterFieldsBasedOnConfig({
          fields: stepFields,
          field_list,
          userType,
        });

        if (step_list?.[step]?.fields) {
          step_list[step].fields = filteredStepFields;
        }
      });
    }

    if (!getIsEnabledLTITools()) {
      updateStepList({
        step_list,
        fieldsToRemove: ["ltiTemplate"],
      });
    }

    return updateAssessmentTemplate({
      t,
      template: dynamicTemplate,
      assessmentType: _.get(assessmentDetails, "assessmentType.value"),
      isFromClassroom: true,
      curriculumType,
      resourceFields: assessmentFields,
      mode: "view",
      plannerElementSets,
      templateId,
      userType,
      assessmentTools: _.get(assessmentDetails, "assessmentTools", []),
    });
  }

  return dynamicTemplate;
};

LEPreview.displayName = "LEPreview";

LEPreview.propTypes = {
  parentType: PropTypes.string,
};

LEPreview.defaultProps = {
  parentType: "UNIT_PLAN",
};

export default compose(
  connect(mapStateToProps, mapActionCreators),
  I18nHOC({ resource: ["unitPlanTemplate", "common"] }),
  graphql(getAssessmentPreviewQuery, {
    name: "getAssessmentPreview",
    skip: ({ assessmentId }) => _.isEmpty(assessmentId),
    options: ({
      assessmentId,
      portalType,
      collaboratorsFilter,
      userArchivedFilters,
    }) => ({
      fetchPolicy: "cache-and-network",
      variables: {
        id: assessmentId,
        portalType,
        collaboratorsFilter,
        userArchivedFilters,
      },
    }),
    props({ getAssessmentPreview, ownProps: { isData, isLoading } }) {
      const assessmentDetails = getAssessmentPreviewDetailsFromCache(
        getAssessmentPreview.variables
      );

      const templateId = _.get(assessmentDetails, "templateId", null);
      const unitPlanTemplateId = _.get(
        assessmentDetails,
        "unitPlan.templateId",
        null
      );

      return {
        assessmentDetails,
        unitPlanTemplateId,
        templateId,
        isData: !_.isEmpty(assessmentDetails) && isData,
        isLoading:
          getAssessmentPreview["networkStatus"] == 1 ||
          getAssessmentPreview["networkStatus"] == 2 ||
          isLoading,
      };
    },
  }),
  graphql(getPlannerTemplateQuery, {
    name: "getPlannerTemplate",
    skip: ({ templateId }) => !templateId,
    options: ({ templateId }) => ({
      fetchPolicy: "cache-and-network",
      variables: {
        ids: [templateId],
      },
    }),
    props({
      getPlannerTemplate,
      ownProps: {
        userType,
        isLoading,
        isData,
        templateId,
        assessmentDetails,
        parentType,
        curriculumType,
        t,
        plannerElementSets,
      },
    }) {
      const unitPlanFields = _.get(assessmentDetails, "unitPlan.fields", []);
      const unitPlanAllFields = _.get(
        assessmentDetails,
        "unitPlan.allFields",
        []
      );
      const assessmentFields = _.get(assessmentDetails, "allFields", []);
      const templates = getPlannerTemplateFromCache(
        getPlannerTemplate.variables,
        true
      );
      const templateFromQuery = _.get(
        _.find(templates, { id: templateId }),
        "body",
        {}
      );

      const template = getDynamicTemplate({
        template: templateFromQuery,
        assessmentFields,
        t,
        userType,
        templateId,
        assessmentDetails,
        curriculumType,
        plannerElementSets,
      });

      const listSteps = GetLESteps({
        userType,
        template,
        resourceDetails: assessmentDetails,
        unitPlanFields,
        parentType,
      });

      return {
        step_list: listSteps.stepList,
        view_steps: listSteps.viewSteps,
        field_list: _.get(template, "field_list", {}),
        assessmentFields,
        unitPlanFields: combineUnitPlanMultiFieldData({
          templateBody: templateFromQuery,
          unitPlanFields: _.cloneDeep(unitPlanFields),
        }),
        unitPlanAllFields,
        isData: !_.isEmpty(templates) && isData,
        isLoading:
          getPlannerTemplate["networkStatus"] == 1 ||
          getPlannerTemplate["networkStatus"] == 2 ||
          isLoading,
      };
    },
  }),
  withLoader
)(LEPreview);
